import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

interface ShoppingListInfo {
  id: number;
  name: string;
}

interface DeleteModalProps {
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
  itemName: string;
  loading?: boolean;
  // Optional: for grocery deletion with usage check
  usedInLists?: ShoppingListInfo[];
  hasMoreLists?: boolean;
  // Optional warning message
  warning?: string;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  open,
  onClose,
  onDelete,
  itemName,
  loading = false,
  usedInLists = [],
  hasMoreLists = false,
  warning,
}) => {
  const hasUsage = usedInLists.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm Deletion</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete <strong>{itemName}</strong>? This action cannot be undone.
        </DialogContentText>

        {warning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {warning}
          </Alert>
        )}

        {hasUsage && (
          <Box sx={{ mt: 2 }}>
            <DialogContentText color="warning.main" sx={{ fontWeight: 'medium' }}>
              This grocery is currently used in the following open shopping lists:
            </DialogContentText>
            <List dense sx={{ mt: 1 }}>
              {usedInLists.map((list) => (
                <ListItem key={list.id} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <RouterLink
                        to={`/shopping-lists/${list.id}`}
                        style={{ color: 'inherit' }}
                        onClick={onClose}
                      >
                        {list.name}
                      </RouterLink>
                    }
                  />
                </ListItem>
              ))}
              {hasMoreLists && (
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary="...and more"
                    primaryTypographyProps={{ color: 'text.secondary', fontStyle: 'italic' }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onDelete}
          color="error"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteModal;